/**
 * Unmatched Payments Card
 *
 * Surfaces payments the ingestion payment-watcher could not reconcile to a
 * launch/mint/session. These are typically double-submits from Xaman or
 * late-arriving payments; they need a manual refund. The card lets an admin
 * review each row, refund manually from the deals wallet, then mark it as
 * refunded (optionally with the on-chain refund tx hash).
 */

import { useEffect, useState, useCallback } from 'react';
import {
  getUnmatchedPayments,
  resolveUnmatchedPayment,
  type UnmatchedPayment,
} from '../../services/adminApi.js';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diff / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function dropsToXrp(drops: string): string {
  const n = Number(drops) / 1_000_000;
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function shortAddr(addr: string): string {
  return addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr;
}

function shortHash(hash: string): string {
  return hash.length > 12 ? `${hash.slice(0, 8)}…` : hash;
}

export function UnmatchedPaymentsCard() {
  const [payments, setPayments] = useState<UnmatchedPayment[]>([]);
  const [unresolved, setUnresolved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [includeResolved, setIncludeResolved] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await getUnmatchedPayments({ minAgeSeconds: 300, includeResolved });
      setPayments(result.payments);
      setUnresolved(result.unresolved);
    } catch (err: any) {
      setError(err.message || 'Failed to load unmatched payments');
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleResolve = async (p: UnmatchedPayment) => {
    const txInput = window.prompt(
      `Refund tx hash (optional 64-char hex). Leave blank if you don't have one yet.\n\nRefunding ${dropsToXrp(
        p.amount_drops
      )} XRP to ${p.sender_address}`
    );
    if (txInput === null) return; // user cancelled
    const refundTxHash = txInput.trim();
    if (refundTxHash && !/^[A-Fa-f0-9]{64}$/.test(refundTxHash)) {
      alert('Tx hash must be 64 hex characters. Aborting.');
      return;
    }
    const notes = window.prompt('Notes (optional)') || undefined;

    setResolvingId(p.id);
    try {
      await resolveUnmatchedPayment(p.id, {
        refund_tx_hash: refundTxHash || undefined,
        notes,
      });
      await load();
    } catch (err: any) {
      alert(`Failed to mark refunded: ${err.message || 'unknown error'}`);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase text-gray-500">
            Unmatched Payments
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Payments the ingestion watcher could not reconcile (older than 5 min).
            Typically duplicate submissions from Xaman — refund manually.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unresolved > 0 ? (
            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              {unresolved} unresolved
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              All caught up
            </span>
          )}
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
            />
            Show resolved
          </label>
          <button
            onClick={load}
            className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</div>
      )}

      {loading && payments.length === 0 ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-green-600">No unmatched payments older than 5 min.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 pr-3 font-medium">Detected</th>
                <th className="py-2 pr-3 font-medium">Sender</th>
                <th className="py-2 pr-3 font-medium">Amount</th>
                <th className="py-2 pr-3 font-medium">Memo</th>
                <th className="py-2 pr-3 font-medium">Tx</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-3 text-gray-700">{timeAgo(p.detected_at)}</td>
                  <td className="py-2 pr-3">
                    <a
                      href={`https://bithomp.com/en/account/${p.sender_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-xs text-blue-600 hover:underline"
                      title={p.sender_address}
                    >
                      {shortAddr(p.sender_address)}
                    </a>
                  </td>
                  <td className="py-2 pr-3 font-medium text-gray-900">
                    {dropsToXrp(p.amount_drops)} XRP
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-600">
                    {p.memo_type ? (
                      <>
                        <span className="font-medium">{p.memo_type}</span>
                        {p.memo_data && (
                          <span className="ml-1 font-mono" title={p.memo_data}>
                            {shortHash(p.memo_data)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <a
                      href={`https://bithomp.com/en/explorer/${p.tx_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-xs text-blue-600 hover:underline"
                      title={p.tx_hash}
                    >
                      {shortHash(p.tx_hash)}
                    </a>
                  </td>
                  <td className="py-2 pr-3">
                    {p.processing_status === 'refunded' ? (
                      <span
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700"
                        title={
                          p.refund_processed_at
                            ? `by ${p.refund_processed_by || 'admin'} at ${new Date(
                                p.refund_processed_at
                              ).toLocaleString()}${p.refund_notes ? ` — ${p.refund_notes}` : ''}`
                            : undefined
                        }
                      >
                        refunded
                        {p.refund_tx_hash ? (
                          <a
                            href={`https://bithomp.com/en/explorer/${p.refund_tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 font-mono text-blue-600 hover:underline"
                          >
                            {shortHash(p.refund_tx_hash)}
                          </a>
                        ) : null}
                      </span>
                    ) : (
                      <span className="rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800">
                        unmatched
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {p.processing_status === 'unmatched' ? (
                      <button
                        onClick={() => handleResolve(p)}
                        disabled={resolvingId === p.id}
                        className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {resolvingId === p.id ? 'Saving…' : 'Mark refunded'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
